/**
 * PAG Hero Slider.
 *
 * Was an inline script in the section. Moved out so it can share the GSAP
 * readiness poll the scrollytelling section already uses, and so it can be
 * re-initialised by the theme editor — as an inline DOMContentLoaded handler it
 * did neither: a blocked GSAP threw on the first `gsap.set`, and adding or
 * editing the section in the editor left a dead slider until a full reload.
 *
 * Autoplay pauses on hover, on keyboard focus, when the tab is hidden and
 * when the banner scrolls off screen, and starts paused under
 * prefers-reduced-motion. The pause reasons are a Set rather than a boolean
 * because they overlap — a pointer leaving must not resume a slider the
 * visitor explicitly paused.
 *
 * Interactivity is governed by GSAP's autoAlpha (opacity + visibility) alone.
 * The section's CSS deliberately does not gate pointer-events on the `active`
 * class: this script never moved that class, so doing so left every slide after
 * the first visible but unclickable once the timer advanced.
 */

const GSAP_TIMEOUT = 3000;
const AUTOPLAY_MS = 3000;
const TRANSITION = 0.85;

function whenGsapReady() {
  return new Promise((resolve) => {
    const started = performance.now();
    (function poll() {
      if (window.gsap) resolve(true);
      else if (performance.now() - started > GSAP_TIMEOUT) resolve(false);
      else requestAnimationFrame(poll);
    })();
  });
}

class HeroSlider {
  #paused = new Set();
  #timer = null;
  #index = 0;
  #animating = false;
  #onVisibility = null;

  constructor(root, gsap) {
    this.root = root;
    this.gsap = gsap;
    this.slides = [...root.querySelectorAll('.pag-slide')];
    this.dotsContainer = root.querySelector('[data-hero-dots]');
    this.prevButton = root.querySelector('.pag-arrow.prev');
    this.nextButton = root.querySelector('.pag-arrow.next');

    // A single slide has nothing to navigate between or pause.
    if (this.slides.length <= 1) {
      [this.prevButton, this.nextButton].forEach((el) => {
        if (el) el.hidden = true;
      });
      return;
    }

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#buildDots();
    this.#bindControls();
    this.#watchVisibility();

    gsap.set(this.slides, { autoAlpha: 0, zIndex: 1, xPercent: 0 });
    gsap.set(this.slides[0], { autoAlpha: 1, zIndex: 2, xPercent: 0 });

    // Reduced motion means no automatic movement at all. The banner still
    // works — arrows and dots are both live — it just never moves on its own.
    if (this.reducedMotion) {
      this.#pause('preference');
    } else {
      this.#resume();
    }
  }

  /* ================================================================== setup */

  #buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.replaceChildren();

    this.dots = this.slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pag-dot';
      dot.setAttribute('aria-label', `Slide ${index + 1} of ${this.slides.length}`);
      if (index === 0) {
        dot.classList.add('active');
        dot.setAttribute('aria-current', 'true');
      }
      dot.addEventListener('click', () => {
        if (index === this.#index || this.#animating) return;
        this.#goTo(index, index > this.#index ? 1 : -1);
        // An explicit choice should be readable, so stop the timer rather than
        // restarting it and yanking the slide away a few seconds later.
        this.#pause('user');
      });
      this.dotsContainer.append(dot);
      return dot;
    });
  }

  #bindControls() {
    this.nextButton?.addEventListener('click', () => this.#nudge(1));
    this.prevButton?.addEventListener('click', () => this.#nudge(-1));

    this.root.addEventListener('pointerenter', () => this.#pause('pointer'));
    this.root.addEventListener('pointerleave', () => this.#resume('pointer'));
    this.root.addEventListener('focusin', () => this.#pause('focus'));
    this.root.addEventListener('focusout', () => this.#resume('focus'));

    this.#onVisibility = () => {
      if (document.hidden) this.#pause('hidden');
      else this.#resume('hidden');
    };
    document.addEventListener('visibilitychange', this.#onVisibility);

    // Left/Right move between slides while the dots have focus.
    this.dotsContainer?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = (this.#index + delta + this.slides.length) % this.slides.length;
      this.#goTo(next, delta);
      this.dots?.[next]?.focus();
      this.#pause('user');
    });

    this.#bindSwipe();
  }

  #bindSwipe() {
    let startX = 0;
    this.root.addEventListener(
      'touchstart',
      (event) => {
        startX = event.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchend',
      (event) => {
        const delta = event.changedTouches[0].screenX - startX;
        if (Math.abs(delta) < 40 || this.#animating) return;
        this.#nudge(delta < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  #watchVisibility() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.#resume('offscreen') : this.#pause('offscreen')),
      { threshold: 0.2 }
    );
    this.observer.observe(this.root);
  }

  /* ============================================================== transport */

  #nudge(direction) {
    if (this.#animating) return;
    const next = (this.#index + direction + this.slides.length) % this.slides.length;
    this.#goTo(next, direction);
    this.#pause('user');
  }

  #pause(reason = 'user') {
    this.#paused.add(reason);
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #resume(reason = null) {
    if (reason) this.#paused.delete(reason);
    if (this.#paused.size || this.#timer) return;
    this.#timer = setInterval(() => {
      this.#goTo((this.#index + 1) % this.slides.length, 1);
    }, AUTOPLAY_MS);
  }

  #goTo(nextIndex, direction = 1) {
    if (this.#animating || nextIndex === this.#index) return;
    this.#animating = true;

    const { gsap } = this;
    const current = this.slides[this.#index];
    const next = this.slides[nextIndex];

    this.dots?.[this.#index]?.classList.remove('active');
    this.dots?.[this.#index]?.removeAttribute('aria-current');
    this.dots?.[nextIndex]?.classList.add('active');
    this.dots?.[nextIndex]?.setAttribute('aria-current', 'true');

    const enterFrom = direction > 0 ? 100 : -100;
    const exitTo = direction > 0 ? -100 : 100;

    gsap.set(next, { autoAlpha: 1, zIndex: 3, xPercent: enterFrom });
    gsap.set(current, { zIndex: 2 });

    const timeline = gsap.timeline({
      onComplete: () => {
        gsap.set(current, { autoAlpha: 0, zIndex: 1, xPercent: 0 });
        gsap.set(next, { xPercent: 0 });
        this.#index = nextIndex;
        this.#animating = false;
      },
    });

    timeline.to(next, { xPercent: 0, duration: TRANSITION, ease: 'power3.inOut' }, 0);
    timeline.to(current, { xPercent: exitTo, duration: TRANSITION, ease: 'power3.inOut' }, 0);

    // Parallax reduced from 25% to 8%. At 25% a full-bleed 1920px image is a
    // large composite every frame for motion nobody is looking at.
    //
    // Skipped entirely for a Product-layout slide: that image is object-fit:
    // contain, sized to be seen in full with nothing cropped off-frame to
    // drift into, inside a column with hard edges (overflow: hidden). The
    // same drift that's imperceptible on a full-bleed cover photo would
    // visibly clip the cutout against its own column during the transition.
    const currentImg = current.classList.contains('pag-slide--product')
      ? null
      : current.querySelector('.pag-slide-img');
    const nextImg = next.classList.contains('pag-slide--product')
      ? null
      : next.querySelector('.pag-slide-img');
    const drift = direction > 0 ? 8 : -8;
    if (nextImg) {
      timeline.fromTo(
        nextImg,
        { xPercent: drift },
        { xPercent: 0, duration: TRANSITION, ease: 'power3.inOut' },
        0
      );
    }
    if (currentImg) {
      timeline.to(currentImg, { xPercent: -drift, duration: TRANSITION, ease: 'power3.inOut' }, 0);
    }

    const catchphrase = next.querySelector('.pag-catchphrase');
    const heading = next.querySelector('.pag-content :is(h1, h2, h3)');
    const sub = next.querySelector('.pag-content p');
    const buttons = next.querySelectorAll('.pag-btn');

    if (catchphrase) {
      timeline.fromTo(
        catchphrase,
        { x: direction > 0 ? 40 : -40, opacity: 0 },
        { x: 0, opacity: 0.85, duration: 0.6, ease: 'power3.out' },
        0.2
      );
    }
    if (heading) {
      timeline.fromTo(
        heading,
        { x: direction > 0 ? 50 : -50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.7, ease: 'power3.out' },
        0.25
      );
    }
    if (sub) {
      timeline.fromTo(
        sub,
        { x: direction > 0 ? 35 : -35, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        0.35
      );
    }
    if (buttons.length) {
      timeline.fromTo(
        buttons,
        { x: direction > 0 ? 25 : -25, opacity: 0, scale: 0.95 },
        { x: 0, opacity: 1, scale: 1, duration: 0.5, stagger: 0.08, ease: 'back.out(1.4)' },
        0.42
      );
    }
  }

  destroy() {
    this.#pause('destroyed');
    this.observer?.disconnect();
    if (this.#onVisibility) document.removeEventListener('visibilitychange', this.#onVisibility);
  }
}

const instances = new WeakMap();

async function boot(scope = document) {
  const sliders = [...(scope.querySelectorAll?.('.pag-hero-slider') ?? [])];
  if (!sliders.length) return;

  // Without GSAP the CSS leaves the first slide visible and the rest hidden —
  // a static banner rather than a broken one. Bail rather than throw.
  if (!(await whenGsapReady())) return;

  sliders.forEach((root) => {
    if (root.dataset.heroReady) return;
    root.dataset.heroReady = 'true';
    instances.set(root, new HeroSlider(root, window.gsap));
  });
}

boot(document);

document.addEventListener('shopify:section:load', (event) => boot(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('.pag-hero-slider').forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
    delete root.dataset.heroReady;
  });
});
